// Currency presentation for MM Web — the SM-443 contract consumed, not
// forked (SM-396 phase 06b).
//
// The contract itself lives in `src/currency`, ported byte-for-byte from
// web-client (only the import specifiers differ, so this console's
// node-based test runner can execute the shared parity fixture). This
// module is MM's thin adapter over it: one seed table for the whole app,
// plus the amount-entry unit the operator forms use.
//
// VALUE stays in src/lib/money.ts (int64 minor units, truncate never
// round). Every currency's mark is operator configuration now, so
// pre-multi-currency figure in this console is unchanged.

// Relative, extensioned imports: this console's test runner is
// `node --experimental-strip-types`, which has no bundler alias
// table, and a rule that cannot be executed by a test is a rule
// nobody is protecting.
import { SPARK_UNIT_MINOR, parseSparkToMinor } from "./money.ts";
import {
  CURRENCY_CODES as CONTRACT_CODES,
  isCurrencyCode as contractIsCurrencyCode,
  monogramGlyph as contractMonogramGlyph,
  resolveCurrencyPresentation,
  type CurrencyCode as ContractCurrencyCode,
} from "../currency/index.ts";

export const CURRENCY_CODES = CONTRACT_CODES;
export type CurrencyCode = ContractCurrencyCode;

export interface CurrencySeed {
  displayName: string;
  symbol: string;
  color: string;
}

/**
 * Frozen seed, DERIVED from the contract rather than repeated. That is
 * what keeps MM Web's unconfigured RED identical to Backoffice's and
 * Web Client's: there is no second table to forget to update.
 */
export const CURRENCY_SEED: Readonly<Record<CurrencyCode, CurrencySeed>> = Object.freeze(
  Object.fromEntries(
    CURRENCY_CODES.map((code) => {
      const p = resolveCurrencyPresentation(code);
      return [code, Object.freeze({ displayName: p.displayName, symbol: p.symbol, color: p.color })];
    }),
  ) as Record<CurrencyCode, CurrencySeed>,
);

export const isCurrencyCode = contractIsCurrencyCode;
export const monogramGlyph = contractMonogramGlyph;

/**
 * A wire payload with no currency predates the epic, and pre-epic money
 * is GOLD. Applied at the PRESENTATION boundary only (G3) — never used
 * to fill in a currency on something being sent.
 */
export function currencyOrGold(code: string | undefined | null): CurrencyCode {
  return isCurrencyCode(code) ? code : "GOLD";
}

/**
 * A wire amount in this console's ENTRY unit — what an operator types.
 *
 * The unit comes from the SM-443 presentation contract (`unitMinor`),
 * which is the ONE place any app decides what one unit of a currency is.
 * Since SM-460 that is the same $1 unit for all three (1 unit = wire
 * 10 000): SM-484 had left GOLD on the legacy cent entry unit, which is
 * why an operator could type "500" into a funding box and move $5. This
 * console and web-backoffice each held a copy of that table, both
 * disagreeing with the contract by 100x; the copies are gone, so there is
 * no longer a second table to keep in sync.
 *
 * Pairs with `parseUnitToMinor` below — the two are inverses, and they
 * live together so a change to one cannot silently miss the other. A
 * form whose display unit and parse unit disagree turns "500" into
 * "50,000" on a round trip.
 */
export function unitFromMinor(minor: number | null | undefined, code: CurrencyCode): string {
  if (minor === undefined || minor === null) return "";
  return String(minor / unitMinorFor(code));
}

/**
 * Inverse of `unitFromMinor`: an operator-typed amount → wire minor units.
 *
 * `parseSparkToMinor` already shifts a typed amount by the module's own display
 * unit (SPARK_UNIT_MINOR); the ratio below re-expresses that in THIS currency's
 * unit. It is 1 for all three currencies today, and it is written as a ratio
 * rather than dropped so that a future currency with a different unit cannot
 * silently inherit GOLD's.
 */
export function parseUnitToMinor(input: string, code: CurrencyCode): number {
  const base = Number(parseSparkToMinor(input)); // input × SPARK_UNIT_MINOR, comma-tolerant
  return base * (unitMinorFor(code) / SPARK_UNIT_MINOR);
}

/**
 * Wire minor units per displayed/entered unit for `code`, from the
 * contract's seed. Not exported: every caller should go through the
 * pair above or through <CurrencyAmount>, and re-exporting a raw divisor
 * is how both consoles grew their own table last time.
 */
function unitMinorFor(code: CurrencyCode): number {
  return resolveCurrencyPresentation(code).unitMinor;
}
